import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import RootLanding from "@/pages/RootLanding";
import FoundersLanding from "@/pages/FoundersLanding";
import Home from "@/pages/Home";
import Campus from "@/pages/Campus";
import Panels from "@/pages/Panels";
import Sprint from "@/pages/Sprint";
import SprintStudio from "@/pages/SprintStudio";
import AspiringLanding from "@/pages/AspiringLanding";
import AIInvestorPage from "@/pages/AIInvestorPage";
import AIPersonaSelector from "@/pages/AIPersonaSelector";
import AIPersonaPage from "@/pages/AIPersonaPage";
import DeckReviewPage from "@/pages/DeckReviewPage";
import ScienceInvadersGame from "@/pages/ScienceInvadersGame";
import Auth from "@/pages/Auth";
import CreateAccount from "@/pages/CreateAccount";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Messaging from "@/pages/Messaging";
import Upload from "@/pages/Upload";
import Analysis from "@/pages/Analysis";
import Investors from "@/pages/Investors";
import InvestorDetail from "@/pages/InvestorDetail";
import FindGrants from "@/pages/FindGrants";
import Collaborators from "@/pages/Collaborators";
import MatchDetail from "@/pages/MatchDetail";
import Events from "@/pages/Events";
import Pharma from "@/pages/Pharma";
import Admin from "@/pages/Admin";
import AdminInvestors from "@/pages/AdminInvestors";
import AdminStrategyQuestions from "@/pages/AdminStrategyQuestions";
import AdminFundingOpportunities from "@/pages/AdminFundingOpportunities";
import AdminEvents from "@/pages/AdminEvents";
import AdminUsers from "@/pages/AdminUsers";
import AdminPharma from "@/pages/AdminPharma";
import AdminAccessRequests from "@/pages/AdminAccessRequests";
import AdminContributions from "@/pages/AdminContributions";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
// Strategy Pages
import MarketStrategy from "@/pages/strategy/MarketStrategy";
import ClinicalStrategy from "@/pages/strategy/ClinicalStrategy";
import TechnologyStrategy from "@/pages/strategy/TechnologyStrategy";
import MilestonesStrategy from "@/pages/strategy/MilestonesStrategy";
import FramingStrategy from "@/pages/strategy/FramingStrategy";
import FundraisingPreparation from "@/pages/fundraising/FundraisingPreparation";
import FundraisingFind from "@/pages/fundraising/FundraisingFind";
import Pipeline from "@/pages/Pipeline";
import FundraisingCall from "@/pages/fundraising/FundraisingCall";
// Aspiring Founder Pages
import AspiringAuth from "@/pages/aspiring/AspiringAuth";
import AspiringAuthCallback from "@/pages/aspiring/AspiringAuthCallback";
import AspiringDashboard from "@/pages/aspiring/AspiringDashboard";
import AspiringProfilePage from "@/pages/aspiring/AspiringProfilePage";
import AspiringCofounderPage from "@/pages/aspiring/AspiringCofounderPage";
import AspiringIdeaPage from "@/pages/aspiring/AspiringIdeaPage";
import AspiringSolutionPage from "@/pages/aspiring/AspiringSolutionPage";
import AspiringAssessmentPage from "@/pages/aspiring/AspiringAssessmentPage";
import AspiringLaunchPage from "@/pages/aspiring/AspiringLaunchPage";
import OperonCard from "@/pages/OperonCard";
import MyNetwork from "@/pages/MyNetwork";
import InvestorIntel from "@/pages/InvestorIntel";
import AdminArea from "@/pages/AdminArea";
import AdminHomepageEditor from "@/pages/AdminHomepageEditor";
import AdminProgrammeEnquiries from "@/pages/AdminProgrammeEnquiries";
import Programmes from "@/pages/Programmes";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    // Check if aspiring founder callback
    if (location.pathname.startsWith('/aspiring')) {
      return <AspiringAuthCallback />;
    }
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public home + product family */}
      <Route path="/" element={<Home />} />
      <Route path="/founders" element={<FoundersLanding />} />
      <Route path="/campus" element={<Campus />} />
      <Route path="/panels" element={<Panels />} />
      <Route path="/sprint" element={<Sprint />} />
      <Route path="/sprint/studio" element={<SprintStudio />} />
      <Route path="/accelerator" element={<Programmes />} />
      <Route path="/programmes" element={<Navigate to="/accelerator" replace />} />
      <Route path="/aspiring-landing" element={<AspiringLanding />} />
      <Route path="/admin-area" element={<AdminArea />} />
      <Route path="/admin-area/homepage-editor" element={<AdminHomepageEditor />} />
      <Route path="/ai-investor" element={<AIInvestorPage />} />
      <Route path="/ai-personas" element={<AIPersonaSelector />} />
      <Route path="/ai-persona/:personaId" element={<AIPersonaPage />} />
      <Route path="/deck-review" element={<DeckReviewPage />} />
      <Route path="/science-invaders" element={<ScienceInvadersGame />} />
      
      {/* Free Tools */}
      <Route path="/intel" element={<InvestorIntel />} />
      <Route path="/investor-intel" element={<InvestorIntel />} />
      
      {/* Founder Auth & App */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/signup" element={<Auth />} />
      <Route path="/create-account" element={<CreateAccount />} />
      <Route path="/verify" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Authenticated app routes — share ONE persistent AppLayout (with Sidebar) that
          stays mounted across route changes. Kills the sidebar flicker + logo reload. */}
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        {/* Strategy Routes */}
        <Route path="/strategy/market" element={<MarketStrategy />} />
        <Route path="/strategy/clinical" element={<ClinicalStrategy />} />
        <Route path="/strategy/technology" element={<TechnologyStrategy />} />
        <Route path="/strategy/milestones" element={<MilestonesStrategy />} />
        <Route path="/strategy/framing" element={<FramingStrategy />} />
        <Route path="/fundraising/preparation" element={<FundraisingPreparation />} />
        <Route path="/fundraising/find" element={<FundraisingFind />} />
        <Route path="/fundraising/crm" element={<Pipeline />} />
        <Route path="/fundraising/call" element={<FundraisingCall />} />
        <Route path="/messaging" element={<Messaging />} />
        <Route path="/upload" element={<Upload />} />
        <Route path="/analysis/:deckId" element={<Analysis />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/investors/:investorId" element={<InvestorDetail />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/grants" element={<FindGrants />} />
        <Route path="/collaborators" element={<Collaborators />} />
        <Route path="/events" element={<Events />} />
        <Route path="/pharma" element={<Pharma />} />
        <Route path="/match/:matchId" element={<MatchDetail />} />
        {/* Admin Routes */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/investors" element={<AdminInvestors />} />
        <Route path="/admin/strategy-questions" element={<AdminStrategyQuestions />} />
        <Route path="/admin/funding-opportunities" element={<AdminFundingOpportunities />} />
        <Route path="/admin/events" element={<AdminEvents />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/pharma" element={<AdminPharma />} />
        <Route path="/admin/access-requests" element={<AdminAccessRequests />} />
        <Route path="/admin/contributions" element={<AdminContributions />} />
        <Route path="/admin/programme-enquiries" element={<AdminProgrammeEnquiries />} />
      </Route>

      {/* Public Card Route */}
      <Route path="/card/:cardId" element={<OperonCard />} />
      {/* Aspiring Founder Routes */}
      <Route path="/aspiring-landing" element={<AspiringLanding />} />
      <Route path="/aspiring/auth" element={<AspiringAuth />} />
      <Route path="/aspiring/callback" element={<AspiringAuthCallback />} />
      <Route path="/aspiring/dashboard" element={<AspiringDashboard />} />
      <Route path="/aspiring/profile" element={<AspiringProfilePage />} />
      <Route path="/aspiring/cofounder" element={<AspiringCofounderPage />} />
      <Route path="/aspiring/idea" element={<AspiringIdeaPage />} />
      <Route path="/aspiring/solution" element={<AspiringSolutionPage />} />
      <Route path="/aspiring/assessment" element={<AspiringAssessmentPage />} />
      <Route path="/aspiring/launch" element={<AspiringLaunchPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />
    </BrowserRouter>
  );
}

export default App;
